import type { CaptureResult, Properties } from 'posthog-js';
import { UserClaims } from '../model/userClaims';
import { getUserRedactionTermsFromClaims, redactTermsInRecord, redactTermsInText } from './redaction';

/**
 * Skill Requirement: mask text, keep structure / keep-list supersedes element_attribute_ignorelist.
 * Allowlisted attrs only — enough for PostHog Actions and heatmaps, no free-text attrs.
 */
const ELEMENT_ATTR_KEEP = new Set([
    'class',
    'id',
    'name',
    'type',
    'role',
    'data-ph-capture',
    'tag_name',
    'classes',
    'nth_child',
    'nth_of_type',
    'attr_id',
    'nth-child',
    'nth-of-type',
]);

/**
 * Skill Requirement: truncate URLs + drop campaign params.
 * Matches utm_*, click IDs, and related keys the SDK lifts out of the landing URL.
 */
const CAMPAIGN_KEYS = /^(utm_|gclid$|fbclid$|li_fat_id$|ph_keyword$|msclkid$|twclid$|ttclid$)/i;

/**
 * Skill Requirement: truncate URLs + drop campaign params; also used when redacting exception stack paths.
 * Keeps origin + pathname only so query/hash (and tokens in them) never ship.
 */
function truncateUrl(value: unknown): unknown {
    if (typeof value !== 'string' || !value) {
        return value;
    }
    try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return value;
        }
        return `${url.origin}${url.pathname}`;
    } catch {
        // Not an absolute http(s) URL. PostHog uses sentinels such as `$direct` for
        // `$referrer`; resolving those against the current origin would turn direct
        // traffic into a fake in-app URL and disagree with `$referring_domain`.
        // Only strip a query/hash if one is actually present.
        const cut = value.search(/[?#]/);
        return cut >= 0 ? value.slice(0, cut) : value;
    }
}

/**
 * Skill Requirement: truncate URLs + drop campaign params; no PII leaves the product.
 * Walks nested props: truncates `$…url` / `$…referrer`, deletes campaign keys
 * (including `$initial_utm_*` / `$session_entry_utm_*` the SDK registers as super props).
 */
function sanitizeUrlBag(obj: Properties | undefined): void {
    if (!obj || typeof obj !== 'object') {
        return;
    }
    for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (CAMPAIGN_KEYS.test(key) || key.startsWith('$initial_utm_') || key.startsWith('$session_entry_utm_')) {
            delete obj[key];
            continue;
        }
        if (key.startsWith('$') && (key.endsWith('url') || key.endsWith('referrer'))) {
            obj[key] = truncateUrl(value);
            continue;
        }
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            sanitizeUrlBag(value as Properties);
        }
    }
}

/**
 * Skill Requirement: keep-list supersedes element_attribute_ignorelist; mask text, keep structure.
 * On one `$elements` item: drop non-allowlisted `attr__*` and bare `href`
 * (attributes survive mask_all_text and can hold customer text).
 */
function stripElementAttrs(element: Record<string, unknown>): void {
    for (const key of Object.keys(element)) {
        if (key.startsWith('attr__')) {
            const attrName = key.slice('attr__'.length);
            if (!ELEMENT_ATTR_KEEP.has(attrName)) {
                delete element[key];
            }
            continue;
        }
        if (key === 'href') {
            delete element[key];
        }
    }
}

/**
 * Skill Requirement: keep-list supersedes element_attribute_ignorelist; mask text, keep structure.
 * Applies the keep-list to `$elements` and `$elements_chain` so one pass covers
 * `$autocapture`, `$rageclick`, and `$dead_click` (dead clicks do not share autocapture config).
 */
function stripUnsafeElementAttrs(properties: Properties): void {
    const elements = properties.$elements;
    if (Array.isArray(elements)) {
        for (const el of elements) {
            if (el && typeof el === 'object') {
                stripElementAttrs(el as Record<string, unknown>);
            }
        }
    }

    const chain = properties.$elements_chain;
    if (typeof chain === 'string' && chain.length > 0) {
        properties.$elements_chain = chain
            .split(';')
            .map((segment) => {
                const colon = segment.indexOf(':');
                if (colon < 0) {
                    return segment;
                }
                const prefix = segment.slice(0, colon + 1);
                const attrs = segment.slice(colon + 1);
                const kept: string[] = [];
                const attrRe = /(\w+(?:-\w+)*)="((?:\\.|[^"\\])*)"/g;
                let match: RegExpExecArray | null;
                while ((match = attrRe.exec(attrs)) !== null) {
                    const name = match[1];
                    const bareName = name.startsWith('attr__') ? name.slice('attr__'.length) : name;
                    if (ELEMENT_ATTR_KEEP.has(bareName) || ELEMENT_ATTR_KEEP.has(name)) {
                        kept.push(`${name}="${match[2]}"`);
                    }
                }
                return `${prefix}${kept.join('')}`;
            })
            .join(';');
    }
}

/**
 * Skill Requirement: never send raw product error messages; no PII leaves the product.
 * Replaces exception message text, truncates stack path URLs, drops `$exception_values`
 * (messages often echo form/customer data).
 */
function redactException(properties: Properties): void {
    const list = properties.$exception_list;
    if (Array.isArray(list)) {
        for (const item of list) {
            if (item && typeof item === 'object') {
                const exception = item as Record<string, unknown>;
                if (typeof exception.value === 'string') {
                    exception.value = '[redacted]';
                }
                const stack = exception.stack;
                if (stack && typeof stack === 'object') {
                    const frames = (stack as { frames?: Array<Record<string, unknown>> }).frames;
                    if (Array.isArray(frames)) {
                        for (const frame of frames) {
                            if (typeof frame.filename === 'string') {
                                frame.filename = truncateUrl(frame.filename) as string;
                            }
                            if (typeof frame.abs_path === 'string') {
                                frame.abs_path = truncateUrl(frame.abs_path) as string;
                            }
                        }
                    }
                }
            }
        }
    }
    delete properties.$exception_values;
}

/**
 * Skill Requirement: no PII leaves the product (claim-term stand-in for patterned redactProperties).
 * Scrubs signed-in user full name / first / last / username from the event name and
 * string properties via helpers in `redaction.ts`.
 */
function applyClaimsRedaction(event: CaptureResult, claims: UserClaims | null | undefined): void {
    const redactionTerms = getUserRedactionTermsFromClaims(claims);
    if (redactionTerms.length === 0) {
        return;
    }

    if (typeof event.event === 'string') {
        event.event = redactTermsInText(event.event, redactionTerms);
    }

    if (event.properties) {
        Object.assign(event.properties, redactTermsInRecord(event.properties as Record<string, unknown>, redactionTerms));
    }
}

/**
 * Skill Requirement: no PII leaves the product; skip deep scrub on `$snapshot`; sanitize `$set` / `$set_once`.
 * PostHog `before_send` entry point. Orchestrates claim redaction, exception scrubbing,
 * URL/campaign hygiene, and the element keep-list. Skips the properties pass for `$snapshot`
 * (replay cost); still sanitizes person bags because they outlive the event on the profile.
 */
export function sanitizePostHogEvent(event: CaptureResult | null, claims?: UserClaims | null): CaptureResult | null {
    if (!event) {
        return event;
    }

    const isSnapshot = event.event === '$snapshot';
    if (!isSnapshot) {
        // `redactTermsInRecord` deep-copies every leaf, so running it over an rrweb
        // `$snapshot_data` payload would rebuild and regex-scan the whole replay
        // buffer on the main thread. Keep it inside the skip, like the rest of the
        // properties pass; `$snapshot` carries no event-name or property PII.
        applyClaimsRedaction(event, claims);
    }
    if (!isSnapshot && event.properties) {
        if (event.event === '$pageview') {
            delete event.properties.title;
        }
        redactException(event.properties);
        sanitizeUrlBag(event.properties);
        stripUnsafeElementAttrs(event.properties);
    }

    const withPersonBags = event as CaptureResult & { $set?: Properties; $set_once?: Properties };
    const redactionTerms = getUserRedactionTermsFromClaims(claims);
    if (withPersonBags.$set && typeof withPersonBags.$set === 'object') {
        sanitizeUrlBag(withPersonBags.$set);
        if (redactionTerms.length > 0) {
            Object.assign(withPersonBags.$set, redactTermsInRecord(withPersonBags.$set as Record<string, unknown>, redactionTerms));
        }
    }
    if (withPersonBags.$set_once && typeof withPersonBags.$set_once === 'object') {
        sanitizeUrlBag(withPersonBags.$set_once);
        if (redactionTerms.length > 0) {
            Object.assign(
                withPersonBags.$set_once,
                redactTermsInRecord(withPersonBags.$set_once as Record<string, unknown>, redactionTerms)
            );
        }
    }

    return event;
}
